import type { ReactNode } from "react";
import { Link } from "react-router";
import { useIntl } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";

/**
 * THE TOP 5 OF A RANKING, for both ranked populations.
 *
 * The third of the shared pieces, beside {@link RankingBandGroups} (the band shell) and
 * {@link RankingRow} (the row). What it owns is the card: the grid that holds at most five of them,
 * the square image field, the rank badge, the score pill and the two-line caption underneath.
 *
 * WHAT SITS IN THE IMAGE FIELD IS THE CALLER'S. An avatar and a company logo are different things
 * with different fallbacks, and neither of the two avatar primitives fits: both draw a fixed round
 * rung, and this field is a square filled edge to edge. The candidate side has always composed it
 * from `<img>` plus the shared monogram style rather than from `Avatar`, and the company side now
 * does the same with its own source — so the geometry is shared while the subject stays local.
 */
export interface RankingTopFiveEntry {
  key: string;
  /** Where the card links to. */
  to: string;
  /** Tooltip of the link, e.g. "Nordwerk GmbH – Profil öffnen". */
  openLabel: string;
  name: string;
  /** The second caption line. A blank one still reserves its height, so cards stay level. */
  subtitle?: string | null;
  /** 0..1, printed as a rounded percent. */
  score: number;
  /** The accessible name of the score pill, already formatted. */
  scoreLabel: string;
  /** Fills the square, edge to edge. */
  image: ReactNode;
}

export function RankingTopFive({
  basis,
  entries,
  emptyHint,
  testId,
  entryTestId,
}: {
  /** What the ranking is by, printed top right. */
  basis: ReactNode;
  entries: readonly RankingTopFiveEntry[];
  /** Shown instead of the grid when nothing qualified. Without one, the section draws nothing. */
  emptyHint?: ReactNode;
  testId: string;
  entryTestId: string;
}) {
  const intl = useIntl();

  return (
    <section className="box bg-surface p-5" data-testid={testId}>
      <BoxHeader
        title={intl.formatMessage({
          id: "match.result.suitability.header",
          defaultMessage: "Top 5 – beste Eignung",
        })}
        right={<span className="text-smycles-micro tracking-[0.2em] text-ink-muted">{basis}</span>}
      />
      {entries.length === 0 ? (
        emptyHint != null ? (
          <div className="rounded-lg border border-dashed border-ink/15 bg-ink/[0.02] px-4 py-5 text-smycles-meta text-ink-muted">
            {emptyHint}
          </div>
        ) : null
      ) : (
        // auto-fit with a 10rem floor and a 12rem ceiling, justify-start: five cards line up from
        // the left at a readable size instead of stretching across the box when there are two.
        <ol className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(10rem,12rem))] justify-start gap-3">
          {entries.map((entry, index) => (
            <li key={entry.key} className="min-w-0" data-testid={entryTestId}>
              <Link
                to={entry.to}
                className="group block overflow-hidden rounded-md bg-surface ring-1 ring-ink/10 outline-none transition hover:ring-accent-decoration/40 focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
                title={entry.openLabel}
              >
                <div className="relative aspect-square w-full overflow-hidden">
                  {entry.image}
                  <span
                    className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-smycles-label font-semibold tabular-nums shadow-sm"
                    style={{ color: "var(--color-on-accent)" }}
                    aria-label={intl.formatMessage(
                      { id: "match.result.suitability.rankLabel", defaultMessage: "Rang {rank}" },
                      { rank: index + 1 },
                    )}
                  >
                    {index + 1}
                  </span>
                  <span
                    className="absolute right-2 top-2 rounded-full bg-surface/90 px-2 py-1 text-smycles-meta font-semibold tabular-nums text-ink shadow-sm backdrop-blur"
                    aria-label={entry.scoreLabel}
                  >
                    {Math.round(entry.score * 100)} %
                  </span>
                </div>
                <div className="min-w-0 bg-surface px-3 py-2.5">
                  <span className="smycles-field-value block truncate text-ink transition group-hover:text-ink-accent">
                    {entry.name}
                  </span>
                  {/* min-h keeps a card without a subtitle the same height as one with it. */}
                  <span
                    className="mt-0.5 block min-h-[1rem] truncate text-smycles-label text-ink-muted"
                    title={entry.subtitle ?? undefined}
                  >
                    {entry.subtitle ?? " "}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
